import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { register } from '../../store/slices/authSlice';
import { geocodingService } from '../../services/geocodingService';
import { legalService } from '../../services/legalService';

// Import our custom icons
import { 
  PersonIcon, 
  EmailIcon, 
  EyeIcon,
  EyeOffIcon,
  PhoneIcon,
  LocationIcon,
  CheckIcon,
  ArrowBackIcon,
  LockIcon
} from '../common';

// Import our Tesla-inspired components
import { TeslaInput, TeslaButton, TeslaCheckbox, TeslaSelect } from '../common';

// Import US States from constants
import { US_STATES } from '../../constants/usStates';

import PasswordRequirements from '../common/PasswordRequirements';
import * as RegRules from '../../utils/registrationFieldRules';
import { toAuthErrorMessage } from '../../utils/authError';
import TurnstileWidget from './TurnstileWidget';

const { FIELD_LIMITS, normalizeEmail } = RegRules;

// Form state interface based on your mobile RegisterScreen design
interface FormState {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  userType: 'client' | 'stylist';
  location: {
    address: string;
    unit?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    servicesAtLocation: boolean;
  };
  stylistProfile: {
    providesMobileService: boolean;
    travelDistance: number;
    specialties: string[];
    salonName: string;
    isSalonManager: boolean;
  };
  agreedToTerms: boolean;
  agreedToProviderAgreement: boolean;
}

// Password requirements are now in centralized utils/passwordValidator.ts

// Define our specialties data
const specialtiesData = [
  { id: 'hair', label: 'Hair Styling', icon: '💇' },
  { id: 'barber', label: 'Barber Services', icon: '💈' },
  { id: 'braiding', label: 'Braiding', icon: '👱‍♀️' },
  { id: 'makeup', label: 'Makeup', icon: '💄' },
  { id: 'nails', label: 'Nails', icon: '💅' },
  { id: 'skincare', label: 'Skincare', icon: '✨' },
  { id: 'massage', label: 'Massage', icon: '💆' },
  { id: 'lashes', label: 'Lashes & Extensions', icon: '👁️' },
  { id: 'brows', label: 'Brows & Shaping', icon: '✏️' },
  { id: 'waxing', label: 'Waxing', icon: '🪒' },
  { id: 'extensions', label: 'Hair Extensions', icon: '👑' },
  { id: 'wellness', label: 'Wellness & Spa', icon: '🧘' },
  { id: 'fitness', label: 'Personal Training', icon: '💪' },
  { id: 'therapy', label: 'Beauty Therapy', icon: '🌸' },
];

const initialFormState: FormState = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  phoneNumber: '',
  userType: 'client',
  location: {
    address: '',
    unit: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    servicesAtLocation: true,
  },
  stylistProfile: {
    providesMobileService: false,
    travelDistance: 0,
    specialties: [],
    salonName: '',
    isSalonManager: false,
  },
  agreedToTerms: false,
  agreedToProviderAgreement: false,
};

const RegistrationForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status, error } = useAppSelector((state) => state.auth);
  const errorMessage = toAuthErrorMessage(error);

  // Initialize form state from URL params if present
  const getInitialFormState = (): FormState => {
    const userTypeParam = searchParams.get('userType');
    
    const initialState = { ...initialFormState };
    
    // Set userType if provided in URL
    if (userTypeParam === 'stylist' || userTypeParam === 'client') {
      initialState.userType = userTypeParam;
    }
    
    return initialState;
  };

  const [formData, setFormData] = useState<FormState>(getInitialFormState());
  const [activeStep, setActiveStep] = useState(0);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isStepValid, setIsStepValid] = useState(false);
  const [justChangedStep, setJustChangedStep] = useState(false);
  const [submitGuardMessage, setSubmitGuardMessage] = useState<string | null>(null);
  const [legalAgreementError, setLegalAgreementError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const submittingRef = React.useRef(false);
  
  // Password strength checking using centralized validator
  // (removed - now using PasswordRequirements component)
  
  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 10) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
    } else if (digits.length >= 6) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    } else if (digits.length >= 3) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
    }
    return digits;
  };
  
  // Format zip code as XXXXX or XXXXX-XXXX
  const formatZipCode = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 5) {
      return `${digits.substring(0, 5)}-${digits.substring(5, 9)}`;
    }
    return digits;
  };

  // Define steps based on user type
  const steps = formData.userType === 'stylist' 
    ? ['Basic Information', 'Address', 'Stylist Details', 'Confirm']
    : ['Basic Information', 'Address', 'Confirm'];

  // Real gating for the final "Create Account" action — not just visual styling.
  // Stylists must additionally accept the Service Provider Agreement.
  const canCreateAccount =
    formData.agreedToTerms && (formData.userType !== 'stylist' || formData.agreedToProviderAgreement);

  const markTouched = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Update field helper using dot notation for nested fields
  const updateField = (field: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev } as any;
      const parts = field.split('.');
      if (parts.length === 1) {
        newData[field] = value;
      } else if (parts.length === 2) {
        const [parent, child] = parts;
        newData[parent] = { ...newData[parent], [child]: value };
      } else if (parts.length === 3) {
        const [parent, child, grandChild] = parts;
        newData[parent] = {
          ...newData[parent],
          [child]: { ...(newData[parent][child] || {}), [grandChild]: value },
        };
      }
      return newData as FormState;
    });
  };

  useEffect(() => {
    let currentErrors: Record<string, string> = {};
    const t = (key: string) => Boolean(touched[key]);

    if (activeStep === 0) {
      currentErrors.name = RegRules.validateFullName(formData.name, t('name'));
      currentErrors.email = RegRules.validateEmailField(formData.email, t('email'));
      currentErrors.password = RegRules.validatePasswordField(formData.password, t('password'));
      currentErrors.confirmPassword = RegRules.validateConfirmPassword(
        formData.confirmPassword,
        formData.password,
        t('confirmPassword')
      );
      currentErrors.phoneNumber = RegRules.validatePhoneUS(formData.phoneNumber, t('phoneNumber'));
    } else if (activeStep === 1) {
      currentErrors['location.address'] = RegRules.validateStreet(
        formData.location.address,
        t('location.address')
      );
      currentErrors['location.city'] = RegRules.validateCityField(
        formData.location.city,
        t('location.city')
      );
      currentErrors['location.state'] = RegRules.validateStateField(
        formData.location.state,
        t('location.state')
      );
      currentErrors['location.zipCode'] = RegRules.validateZipField(
        formData.location.zipCode,
        t('location.zipCode')
      );
      currentErrors['location.unit'] = t('location.unit')
        ? RegRules.validateUnitField(formData.location.unit)
        : '';
    } else if (activeStep === 2 && formData.userType === 'stylist') {
      currentErrors['stylistProfile.salonName'] = RegRules.validateSalonNameField(
        formData.stylistProfile.salonName,
        t('stylistProfile.salonName'),
        true
      );
      currentErrors['stylistProfile.specialties'] = RegRules.validateSpecialtiesField(
        formData.stylistProfile.specialties,
        t('stylistProfile.specialties'),
        true
      );
      currentErrors['stylistProfile.travelDistance'] = RegRules.validateTravelDistanceMiles(
        formData.stylistProfile.travelDistance,
        formData.stylistProfile.providesMobileService,
        t('stylistProfile.travelDistance')
      );
    }
    setErrors(currentErrors);
    setIsStepValid(Object.values(currentErrors).every((err) => !err));
  }, [formData, activeStep, touched]);

  const handleNext = () => {
    const stepFields =
      activeStep === 0
        ? ['name', 'email', 'password', 'confirmPassword', 'phoneNumber']
        : activeStep === 1
        ? ['location.address', 'location.city', 'location.state', 'location.zipCode']
        : activeStep === 2 && formData.userType === 'stylist'
        ? ['stylistProfile.salonName', 'stylistProfile.specialties'].concat(
            formData.stylistProfile.providesMobileService ? ['stylistProfile.travelDistance'] : []
          )
        : [];
    const newTouched = { ...touched };
    stepFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);
    if (isStepValid) {
      // Prevent accidental submission right after changing steps
      setJustChangedStep(true);
      setActiveStep((prev) => prev + 1);
      setTimeout(() => {
        setJustChangedStep(false);
      }, 500);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Prevent Enter key from submitting form on non-final steps
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      const isLastStep = activeStep === steps.length - 1;
      if (!isLastStep) {
        e.preventDefault();
        // If on a non-final step and form is valid, advance to next step
        if (isStepValid) {
          handleNext();
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent accidental submission if we just changed steps
    if (justChangedStep) {
      return; // Prevent accidental submission
    }
    
    // Only allow submission on the final step
    const isLastStep = activeStep === steps.length - 1;
    if (!isLastStep) {
      return; // Don't submit if not on final step
    }
    
    if (!isStepValid) return;

    // Hard gate: cannot submit without agreeing to required legal documents,
    // regardless of how the form was submitted (click, Enter key, etc.).
    if (!canCreateAccount) {
      setLegalAgreementError(
        !formData.agreedToTerms
          ? 'You must agree to the Terms of Service and Privacy Policy to create an account.'
          : 'You must agree to the Service Provider Agreement to register as a stylist.'
      );
      return;
    }
    setLegalAgreementError(null);

    if (!turnstileToken || submittingRef.current) return;

    const hardErrors = [
      RegRules.validateFullName(formData.name, true),
      RegRules.validateEmailField(formData.email, true),
      RegRules.validatePasswordField(formData.password, true),
      RegRules.validateConfirmPassword(formData.confirmPassword, formData.password, true),
      RegRules.validatePhoneUS(formData.phoneNumber, true),
      RegRules.validateStreet(formData.location.address, true),
      RegRules.validateCityField(formData.location.city, true),
      RegRules.validateStateField(formData.location.state, true),
      RegRules.validateZipField(formData.location.zipCode, true),
      RegRules.validateUnitField(formData.location.unit),
      ...(formData.userType === 'stylist'
        ? [
            RegRules.validateSalonNameField(formData.stylistProfile.salonName, true, true),
            RegRules.validateSpecialtiesField(formData.stylistProfile.specialties, true, true),
            ...(formData.stylistProfile.providesMobileService
              ? [
                  RegRules.validateTravelDistanceMiles(
                    formData.stylistProfile.travelDistance,
                    true,
                    true
                  ),
                ]
              : []),
          ]
        : []),
    ].filter(Boolean);
    if (hardErrors.length) {
      setSubmitGuardMessage(hardErrors[0]!);
      return;
    }
    setSubmitGuardMessage(null);
    
    // Clean phone number - remove all non-digit characters
    const cleanPhoneNumber = formData.phoneNumber.replace(/\D/g, '');
    
    // Get affiliate referral code from sessionStorage if available
    const affiliateReferralCode = sessionStorage.getItem('affiliateReferralCode');
    const affiliateClickId = sessionStorage.getItem('affiliateClickId');
    const affiliateSessionId = sessionStorage.getItem('affiliateSessionId');
    
    // Prepare base registration data
    const registrationData: any = {
      name: formData.name.trim(),
      email: normalizeEmail(formData.email),
      password: formData.password,
      phoneNumber: cleanPhoneNumber,
      role: formData.userType,
      userType: formData.userType, // Add userType field to match mobile app's registration payload
      registrationSource: 'web',
      turnstileToken,
    };
    
    // Add affiliate tracking data if available
    if (affiliateReferralCode) {
      registrationData.affiliateReferralCode = affiliateReferralCode;
      if (affiliateClickId) {
        registrationData.affiliateClickId = affiliateClickId;
      }
      if (affiliateSessionId) {
        registrationData.affiliateSessionId = affiliateSessionId;
      }
      console.log('Including affiliate tracking in registration:', {
        referralCode: affiliateReferralCode,
        clickId: affiliateClickId,
        sessionId: affiliateSessionId
      });
    }
    
    // Clean zip code - remove all non-digit characters
    const cleanZipCode = formData.location.zipCode.replace(/\D/g, '');
    
    // Process location with geocoding
    let coordinates = null;
    if (
      formData.location.address &&
      formData.location.city &&
      formData.location.state &&
      cleanZipCode
    ) {
      try {
        console.log('📍 Geocoding address during registration:', {
          address: formData.location.address,
          city: formData.location.city,
          state: formData.location.state,
          zipCode: cleanZipCode
        });
        
        // Geocode the address to get coordinates
        const coords = await geocodingService.geocodeAddress(
          formData.location.address,
          formData.location.city,
          formData.location.state,
          cleanZipCode
        );
        
        if (coords) {
          // Format coordinates for MongoDB (GeoJSON: [longitude, latitude])
          coordinates = { type: 'Point', coordinates: [coords.lng, coords.lat] };
          console.log('✅ Geocoding successful:', coordinates);
        } else {
          console.warn('⚠️  Geocoding returned null - coordinates will not be set. User should update location after registration.');
        }
      } catch (geoError) {
        console.error('❌ Geocoding error:', geoError);
        // Don't set coordinates if geocoding fails - user can update location later
      }
    } else {
      console.warn('⚠️  Incomplete address information - skipping geocoding');
    }
    
    // Create location data with coordinates (only if geocoding succeeded)
    const locationData = {
      ...formData.location,
      address: formData.location.address.trim(),
      city: formData.location.city.trim(),
      state: formData.location.state.trim(),
      unit: formData.location.unit?.trim(),
      zipCode: cleanZipCode,
      ...(coordinates && { coordinates })
    };
    
    // Warn if coordinates are missing
    if (!coordinates) {
      console.warn('⚠️  No coordinates available - user will need to update location after registration');
    }
    
    // Save extra registration data to localStorage for the redux action to use
    // This keeps large objects out of the URL and matches the mobile app's approach
    const extraData = {
      location: locationData,
      // Explicitly include userType to ensure it's preserved
      userType: formData.userType,
      ...(formData.userType === 'stylist' && { stylistProfile: formData.stylistProfile }),
    };
    
    localStorage.setItem('registrationExtra', JSON.stringify(extraData));
    
    submittingRef.current = true;
    try {
      // Submit registration
      const result = await dispatch(register(registrationData)).unwrap();

      // Record legal acceptance now that we have an authenticated session.
      // Best-effort: a failure here must not block account creation, which
      // already succeeded — but we do log it so it can be investigated.
      try {
        const acceptancePromises = [
          legalService.recordAcceptance('terms-of-service'),
          legalService.recordAcceptance('privacy-policy'),
        ];
        if (formData.userType === 'stylist') {
          acceptancePromises.push(legalService.recordAcceptance('provider-agreement'));
        }
        await Promise.all(acceptancePromises);
      } catch (acceptanceError) {
        console.error('Failed to record legal document acceptance:', acceptanceError);
      }
      
      // Clear referral code from sessionStorage after successful registration
      // (it's already been sent to backend and associated with the account)
      if (affiliateReferralCode) {
        console.log('Registration successful with referral code, clearing session storage');
        // Keep it in sessionStorage for booking attribution, but mark as registered
        sessionStorage.setItem('affiliateReferralRegistered', 'true');
      }
      
      // Check if email verification is required
      if (result && result.user && !result.user.emailVerified) {
        // Email not verified - redirect to verification page
        console.log('Email not verified, redirecting to verification page');
        navigate('/verify-email', {
          state: {
            email: normalizeEmail(formData.email),
            name: formData.name.trim()
          }
        });
        return;
      }
      
      // Email is verified - navigate to appropriate dashboard based on user type
      const dashboardRoute = formData.userType === 'stylist' 
        ? '/stylist/dashboard' 
        : '/client/dashboard';
      
      navigate(dashboardRoute);
    } catch (err: any) {
      // Error handling matches mobile app
      if (err.data && err.data.details) {
        // If server returns validation errors, display them
        const serverErrors: Record<string, string> = {};
        err.data.details.forEach((detail: any) => {
          const field = detail.path.join('.');
          serverErrors[field] = detail.message;
        });
        setErrors({...errors, ...serverErrors});
      } else {
        console.error('Registration error:', err);
      }
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
    } finally {
      submittingRef.current = false;
    }
  };

  // Step indicators
  const StepIndicator = () => (
    <div className="auth-stepper">
      {steps.map((label, index) => (
        <div 
          key={label} 
          className={`auth-stepper__step ${activeStep === index ? 'active' : ''} ${activeStep > index ? 'completed' : ''}`}
        >
          <div className="auth-stepper__indicator">
            {activeStep > index ? (
              <CheckIcon size={16} color="white" />
            ) : (
              <span>{index + 1}</span>
            )}
          </div>
          <div className="auth-stepper__label">{label}</div>
          {index < steps.length - 1 && (
            <div className={`auth-stepper__connector ${activeStep > index ? 'completed' : ''}`} />
          )}
        </div>
      ))}
    </div>
  );

  // Render Basic Info Step
  const renderBasicInfoStep = () => (
    <div className="auth-form__section">
      <div className="auth-form__section-title">
        I am registering as: *
      </div>
      
      <div className="user-type-selector">
        <div 
          className={`user-type-card ${formData.userType === 'client' ? 'active' : ''}`}
          onClick={() => updateField('userType', 'client')}
        >
          <PersonIcon 
            size={32} 
            color={formData.userType === 'client' ? '#e82127' : '#97a0af'} 
          />
          <span>Client</span>
        </div>
        
        <div 
          className={`user-type-card ${formData.userType === 'stylist' ? 'active' : ''}`}
          onClick={() => updateField('userType', 'stylist')}
        >
          <LocationIcon 
            size={32} 
            color={formData.userType === 'stylist' ? '#e82127' : '#97a0af'} 
          />
          <span>Stylist</span>
        </div>
      </div>
      
      <div className="auth-form__section-title">
        Personal Information
      </div>
      
      <TeslaInput
        id="name"
        name="name"
        type="text"
        label="Full Name"
        showRequired
        icon={<PersonIcon />}
        value={formData.name}
        onChange={(e) => updateField('name', e.target.value)}
        onBlur={() => markTouched('name')}
        error={touched.name ? errors.name : ''}
        fullWidth
        maxLength={FIELD_LIMITS.fullName}
      />
      
      <TeslaInput
        id="email"
        name="email"
        type="email"
        label="Email Address"
        showRequired
        icon={<EmailIcon />}
        value={formData.email}
        onChange={(e) => updateField('email', e.target.value)}
        onBlur={() => markTouched('email')}
        error={touched.email ? errors.email : ''}
        fullWidth
        maxLength={FIELD_LIMITS.email}
      />
      
      <TeslaInput
        id="password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        label="Password"
        showRequired
        icon={<LockIcon />}
        actionIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
        onActionClick={() => setShowPassword(!showPassword)}
        value={formData.password}
        onChange={(e) => updateField('password', e.target.value)}
        onBlur={() => markTouched('password')}
        error={touched.password ? errors.password : ''}
        fullWidth
      />
      
      {/* Password Strength Indicator - Using centralized component */}
      {formData.password && (
        <PasswordRequirements password={formData.password} />
      )}
      
      <TeslaInput
        id="confirmPassword"
        name="confirmPassword"
        type={showConfirmPassword ? 'text' : 'password'}
        label="Confirm Password"
        showRequired
        icon={<LockIcon />}
        actionIcon={showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
        onActionClick={() => setShowConfirmPassword(!showConfirmPassword)}
        value={formData.confirmPassword}
        onChange={(e) => updateField('confirmPassword', e.target.value)}
        onBlur={() => markTouched('confirmPassword')}
        error={touched.confirmPassword ? errors.confirmPassword : ''}
        fullWidth
      />
      
      <TeslaInput
        id="phoneNumber"
        name="phoneNumber"
        type="tel"
        label="Phone Number"
        showRequired
        icon={<PhoneIcon />}
        value={formData.phoneNumber}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          updateField('phoneNumber', formatted);
        }}
        onBlur={() => markTouched('phoneNumber')}
        error={touched.phoneNumber ? errors.phoneNumber : ''}
        fullWidth
        placeholder="(XXX) XXX-XXXX"
      />
      
      <div className="auth-form__helper-text">
        <p>Fields marked with * are required. Password must meet all requirements above.</p>
      </div>
    </div>
  );

  // Render Address Step
  const renderAddressStep = () => (
    <div className="auth-form__section">
      <div className="auth-form__section-title">
        Where are you located?
      </div>
      
      <TeslaInput
        id="address"
        name="address"
        type="text"
        label="Street Address"
        showRequired
        icon={<LocationIcon />}
        value={formData.location.address}
        onChange={(e) => updateField('location.address', e.target.value)}
        onBlur={() => markTouched('location.address')}
        error={touched['location.address'] ? errors['location.address'] : ''}
        fullWidth
        maxLength={FIELD_LIMITS.street}
      />
      
      <TeslaInput
        id="unit"
        name="unit"
        type="text"
        label="Unit/Suite (Optional)"
        value={formData.location.unit || ''}
        onChange={(e) => updateField('location.unit', e.target.value.slice(0, FIELD_LIMITS.unit))}
        onBlur={() => markTouched('location.unit')}
        error={touched['location.unit'] ? errors['location.unit'] : ''}
        fullWidth
        maxLength={FIELD_LIMITS.unit}
      />
      
      <div className="auth-form__row">
        <div className="auth-form__col col-lg">
          <TeslaInput
            id="city"
            name="city"
            type="text"
            label="City"
            showRequired
            value={formData.location.city}
            onChange={(e) => updateField('location.city', e.target.value)}
            onBlur={() => markTouched('location.city')}
            error={touched['location.city'] ? errors['location.city'] : ''}
            fullWidth
            maxLength={FIELD_LIMITS.city}
          />
        </div>
        
        <div className="auth-form__col col-sm">
          <TeslaSelect
            id="state"
            label="State"
            showRequired
            value={formData.location.state}
            onChange={(value) => {
              updateField('location.state', value);
              markTouched('location.state');
            }}
            options={US_STATES}
            placeholder="Select state"
            error={touched['location.state'] ? errors['location.state'] : ''}
            fullWidth
            searchable={true}
            searchPlaceholder="Type to search states..."
          />
        </div>
        
        <div className="auth-form__col col-sm">
          <TeslaInput
            id="zipCode"
            name="zipCode"
            type="text"
            label="ZIP Code"
            showRequired
            value={formData.location.zipCode}
            onChange={(e) => {
              const formatted = formatZipCode(e.target.value);
              updateField('location.zipCode', formatted);
            }}
            onBlur={() => markTouched('location.zipCode')}
            error={touched['location.zipCode'] ? errors['location.zipCode'] : ''}
            fullWidth
            placeholder="XXXXX or XXXXX-XXXX"
            maxLength={FIELD_LIMITS.zipDisplay}
          />
        </div>
      </div>
      
      {formData.userType === 'stylist' && (
        <div className="auth-form__checkbox-section">
          <TeslaCheckbox
            id="servicesAtLocation"
            label="I provide services at this location"
            checked={formData.location.servicesAtLocation}
            onChange={(e) => updateField('location.servicesAtLocation', e.target.checked)}
          />
        </div>
      )}
      
      <div className="auth-form__helper-text">
        <p>Your location helps clients find stylists near them.</p>
      </div>
    </div>
  );

  // Render Stylist Details Step
  const renderStylistDetailsStep = () => (
    <div className="auth-form__section">
      <div className="auth-form__section-title">
        Tell us about your business
      </div>
      
      <TeslaInput
        id="salonName"
        name="salonName"
        type="text"
        label="Salon Name"
        showRequired
        icon={<LocationIcon />}
        value={formData.stylistProfile.salonName}
        onChange={(e) => updateField('stylistProfile.salonName', e.target.value)}
        onBlur={() => markTouched('stylistProfile.salonName')}
        error={touched['stylistProfile.salonName'] ? errors['stylistProfile.salonName'] : ''}
        fullWidth
        maxLength={FIELD_LIMITS.salonName}
      />
      
      <div className="auth-form__checkbox-section">
        <TeslaCheckbox
          id="isSalonManager"
          label="I manage this salon"
          checked={formData.stylistProfile.isSalonManager}
          onChange={(e) => updateField('stylistProfile.isSalonManager', e.target.checked)}
        />
      </div>
      
      <div className="auth-form__divider"></div>
      
      <div className="auth-form__section-title">
        Mobile Services
      </div>
      
      <div className="auth-form__checkbox-section">
        <TeslaCheckbox
          id="providesMobileService"
          label="I provide mobile services to clients"
          checked={formData.stylistProfile.providesMobileService}
          onChange={(e) => updateField('stylistProfile.providesMobileService', e.target.checked)}
        />
      </div>
      
      {formData.stylistProfile.providesMobileService && (
        <TeslaInput
          id="travelDistance"
          name="travelDistance"
          type="number"
          label="How far will you travel? (miles)"
          showRequired
          value={formData.stylistProfile.travelDistance || ''}
          onChange={(e) => updateField('stylistProfile.travelDistance', parseInt(e.target.value, 10) || 0)}
          onBlur={() => markTouched('stylistProfile.travelDistance')}
          error={touched['stylistProfile.travelDistance'] ? errors['stylistProfile.travelDistance'] : ''}
          fullWidth
        />
      )}
      
      <div className="auth-form__divider"></div>
      
      <div className="auth-form__section-title">
        Your Specialties *
      </div>
      
      {touched['stylistProfile.specialties'] && errors['stylistProfile.specialties'] && (
        <div className="input-error" role="alert">
          {errors['stylistProfile.specialties']}
        </div>
      )}
      
      <div className="specialty-chips">
        {specialtiesData.map((specialty) => {
          const selected = formData.stylistProfile.specialties.includes(specialty.id);
          return (
            <div 
              key={specialty.id}
              className={`specialty-chip ${selected ? 'active' : ''}`}
              onClick={() => {
                const current = [...formData.stylistProfile.specialties];
                if (selected) {
                  const index = current.indexOf(specialty.id);
                  current.splice(index, 1);
                } else {
                  current.push(specialty.id);
                }
                markTouched('stylistProfile.specialties');
                updateField('stylistProfile.specialties', current);
              }}
            >
              <span className="specialty-chip__icon">{specialty.icon}</span>
              <span className="specialty-chip__label">{specialty.label}</span>
            </div>
          );
        })}
      </div>
      
      {formData.stylistProfile.specialties.length === 0 && !touched['stylistProfile.specialties'] && (
        <div className="auth-form__hint">
          Select at least one specialty to help clients find you
        </div>
      )}
    </div>
  );

  // Render Confirmation Step
  const renderConfirmationStep = () => (
    <div className="auth-form__section">
      <div className="confirm-header">
        <CheckIcon color="#e82127" size={48} />
        <h2>Almost There!</h2>
        <p>Please review your information before submitting.</p>
      </div>
      
      <div className="confirm-card">
        <div className="confirm-section">
          <div className="confirm-title">Account Type</div>
          <div className="confirm-value accent">{formData.userType === 'client' ? 'Client' : 'Stylist'}</div>
        </div>
        
        <div className="auth-form__divider"></div>
        
        <div className="confirm-section">
          <div className="confirm-title">Personal Information</div>
          <div className="confirm-item">
            <span className="confirm-label">Name:</span>
            <span className="confirm-value">{formData.name}</span>
          </div>
          <div className="confirm-item">
            <span className="confirm-label">Email:</span>
            <span className="confirm-value">{formData.email}</span>
          </div>
          <div className="confirm-item">
            <span className="confirm-label">Phone:</span>
            <span className="confirm-value">{formData.phoneNumber}</span>
          </div>
        </div>
        
        <div className="auth-form__divider"></div>
        
        <div className="confirm-section">
          <div className="confirm-title">Location</div>
          <div className="confirm-item">
            <span className="confirm-value">
              {formData.location.address}
              {formData.location.unit ? `, Unit ${formData.location.unit}` : ''}
            </span>
          </div>
          <div className="confirm-item">
            <span className="confirm-value">
              {formData.location.city}, {formData.location.state} {formData.location.zipCode}
            </span>
          </div>
        </div>
        
        {formData.userType === 'stylist' && (
          <>
            <div className="auth-form__divider"></div>
            
            <div className="confirm-section">
              <div className="confirm-title">Business Details</div>
              <div className="confirm-item">
                <span className="confirm-label">Salon:</span>
                <span className="confirm-value">{formData.stylistProfile.salonName}</span>
              </div>
              <div className="confirm-item">
                <span className="confirm-label">Mobile Services:</span>
                <span className="confirm-value">
                  {formData.stylistProfile.providesMobileService 
                    ? `Yes, within ${formData.stylistProfile.travelDistance} miles` 
                    : 'No'}
                </span>
              </div>
            </div>
            
            <div className="auth-form__divider"></div>
            
            {formData.stylistProfile.specialties.length > 0 && (
              <>
                <div className="auth-form__divider"></div>
                
                <div className="confirm-section">
                  <div className="confirm-title">Specialties</div>
                  <div className="confirm-specialties">
                    {formData.stylistProfile.specialties.map(id => {
                      const specialty = specialtiesData.find(s => s.id === id);
                      return specialty ? (
                        <div key={id} className="confirm-specialty">
                          <span>{specialty.icon}</span>
                          <span>{specialty.label}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      
      <div className="auth-form__checkbox-section">
        <TeslaCheckbox
          id="agreedToTerms"
          label={
            <span>
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                Privacy Policy
              </Link>
            </span>
          }
          checked={formData.agreedToTerms}
          onChange={(e) => {
            updateField('agreedToTerms', e.target.checked);
            setLegalAgreementError(null);
          }}
        />
      </div>

      {formData.userType === 'stylist' && (
        <div className="auth-form__checkbox-section">
          <TeslaCheckbox
            id="agreedToProviderAgreement"
            label={
              <span>
                I agree to the{' '}
                <Link
                  to="/provider-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Service Provider Agreement
                </Link>
              </span>
            }
            checked={formData.agreedToProviderAgreement}
            onChange={(e) => {
              updateField('agreedToProviderAgreement', e.target.checked);
              setLegalAgreementError(null);
            }}
          />
        </div>
      )}

      {legalAgreementError && (
        <div className="input-error" role="alert">
          {legalAgreementError}
        </div>
      )}
    </div>
  );

  // Get content for current step
  const getStepContent = (step: number) => {
    if (step === 0) return renderBasicInfoStep();
    if (step === 1) return renderAddressStep();
    if (step === 2) {
      // For stylists, step 2 is Stylist Details; for clients, step 2 is Confirm
      return formData.userType === 'stylist' ? renderStylistDetailsStep() : renderConfirmationStep();
    }
    if (step === 3) {
      // Step 3 is only for stylists (Confirm step)
      return renderConfirmationStep();
    }
    return renderConfirmationStep();
  };

  return (
    <div className="auth-form-container">
      {submitGuardMessage && (
        <div className="auth-form__error-banner" role="alert">
          {submitGuardMessage}
        </div>
      )}
      {errorMessage && (
        <div className="auth-form__error-banner" role="alert">
          {errorMessage}
        </div>
      )}
      
      <StepIndicator />
      
      <form className="auth-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        {getStepContent(activeStep)}
        
        {activeStep === steps.length - 1 && (
          <TurnstileWidget
            resetSignal={turnstileReset}
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken('')}
            onError={() => setTurnstileToken('')}
          />
        )}

        <div className="auth-form__actions">
          {activeStep > 0 && (
            <TeslaButton 
              type="button" 
              variant="outline"
              icon={<ArrowBackIcon size={16} />}
              onClick={handleBack}
            >
              Back
            </TeslaButton>
          )}
          
          {activeStep < steps.length - 1 ? (
            <TeslaButton 
              type="button" 
              variant="primary"
              onClick={handleNext} 
              disabled={!isStepValid}
            >
              Next
            </TeslaButton>
          ) : (
            <TeslaButton
              type="submit"
              variant="primary"
              loading={status === 'loading'}
              disabled={status === 'loading' || !isStepValid || !canCreateAccount || !turnstileToken}
            >
              Create Account
            </TeslaButton>
          )}
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;